import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useServerTable } from '@/hooks/use-server-table';
import { mergeWhere } from '@/lib/table-query';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui-kit/tabs';
import { ASSESSMENT_TYPES, AssessmentTypeKey } from '@/modules/shared/hr-form-engine';
import {
  useDepartmentOptions,
  useDepartmentScope,
  useDesignationOptions,
  useEmployeesByUserIds,
} from '@/modules/shared/hr-lookups';
import {
  buildReviewFilter,
  DEFAULT_PM_REVIEWS_ORDER,
  EMPTY_REVIEW_FILTERS,
  PmReview,
  ReviewFilters,
  ReviewFilterValue,
  useGetPmReviews,
} from '@/modules/performance/hr-review-core';
import AdvanceDataTable from '@/components/core/advance-data-table/advance-data-table';
import { createReviewsTableColumns } from '../../components/reviews-table/reviews-table-columns';
import { PageHeader } from '@/components/core';

const AssessmentTypeTab = ({ assessmentType }: { assessmentType: AssessmentTypeKey }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { pagination, resetPage, order, columnWhere, tableProps } = useServerTable({
    pageSize: 15,
    defaultOrder: DEFAULT_PM_REVIEWS_ORDER,
  });
  const [filters, setFilters] = useState<ReviewFilterValue>(EMPTY_REVIEW_FILTERS);

  const { data: departments = [] } = useDepartmentOptions();
  const { getDepartmentIdCondition, isLoading: isScopeLoading } = useDepartmentScope();
  const { data: designations = [] } = useDesignationOptions();
  const departmentNames = useMemo(
    () => new Map(departments.map((option) => [option.ItemId, option.Title])),
    [departments]
  );
  const designationNames = useMemo(
    () => new Map(designations.map((option) => [option.ItemId, option.Title])),
    [designations]
  );

  const { data, isLoading, error } = useGetPmReviews({
    pageNo: pagination.pageIndex + 1,
    pageSize: pagination.pageSize,
    filter: mergeWhere(
      buildReviewFilter(
        filters,
        { AssessmentType: assessmentType },
        getDepartmentIdCondition(filters.departmentId)
      ),
      columnWhere
    ),
    order,
  });
  const { byUserId: employeesByUserId } = useEmployeesByUserIds(
    (data?.items ?? []).map((review) => review.ReviewForId)
  );
  const emailOf = (userId: string) => employeesByUserId.get(userId)?.Email;

  const handleFiltersChange = (next: ReviewFilterValue) => {
    setFilters(next);
    resetPage();
  };

  const columns = createReviewsTableColumns({ t, departmentNames, designationNames, emailOf });

  return (
    <div className="flex flex-col gap-4">
      <ReviewFilters value={filters} onChange={handleFiltersChange} />
      <AdvanceDataTable
        {...tableProps}
        data={data?.items ?? []}
        columns={columns}
        onRowClick={(review: PmReview) => navigate(`/hr/reviews/${review.ItemId}`)}
        // A department-limited user's list is only right once their departments are known.
        isLoading={isLoading || isScopeLoading}
        error={error instanceof Error ? error : null}
        pagination={{
          pageIndex: pagination.pageIndex,
          pageSize: pagination.pageSize,
          totalCount: data?.totalCount ?? 0,
        }}
        isExpandRowContent={false}
        mobileView="cards"
      />
    </div>
  );
};

export const ReviewsListPage = () => {
  const { t } = useTranslation();

  return (
    <div className="flex w-full flex-col gap-4">
      <PageHeader title={t('PERFORMANCE_REVIEWS')} />

      <Tabs defaultValue={ASSESSMENT_TYPES[0].key}>
        <TabsList>
          {ASSESSMENT_TYPES.map((option) => (
            <TabsTrigger key={option.key} value={option.key}>
              {t(option.title)}
            </TabsTrigger>
          ))}
        </TabsList>
        {ASSESSMENT_TYPES.map((option) => (
          <TabsContent key={option.key} value={option.key}>
            <AssessmentTypeTab assessmentType={option.key} />
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
};
